import { useCallback, useEffect, useRef, useState } from "react";
import { useDrillStore } from "@/application/stores/drillStore";
import type { Hint } from "@/domain/learning/model/hint";
import { findCompanionReaction } from "@/domain/story/find-companion-reaction";
import type { CompanionReaction, ReactionTrigger } from "@/domain/story/model/companion-reaction";
import { reactionVoiceAssetPath } from "@/infrastructure/assets/paths";
import type { KeystrokeOutcome } from "@/presentation/features/drill/keystroke-log";

/** 90秒の無操作で師範代から声をかける（ui-design.md §5.7）。 */
const IDLE_TIMEOUT_MS = 90_000;

/** 3回続けての未達で「repeated-fail」の声かけに切り替える（`over-limit`単発とは別扱い）。 */
const REPEATED_FAIL_THRESHOLD = 3;

/** 雑談・コツを挟む間隔。声かけが立つたびに数え直す（続けざまに重ねない）。 */
const CHATTER_INTERVAL_MS = 45_000;

/** 吹き出しの寿命の下限・上限と、1文字あたりの読み時間。 */
const BUBBLE_MIN_MS = 4_000;
const BUBBLE_MAX_MS = 14_000;
const BUBBLE_MS_PER_CHAR = 90;

/**
 * 吹き出しを出しておく時間。**出しっぱなしにしない**——いつ言われたものかが分からなくなり、
 * 古い台詞が画面に居座ってしまうため、読み終える頃に自分から引っ込める。
 */
function bubbleLifetimeMs(reaction: CompanionReaction): number {
  const charCount = reaction.lines.reduce((sum, line) => sum + line.text.length, 0);
  return Math.min(BUBBLE_MAX_MS, Math.max(BUBBLE_MIN_MS, charCount * BUBBLE_MS_PER_CHAR));
}

export interface ActiveReaction {
  readonly id: number;
  readonly reaction: CompanionReaction;
  /** 再生する師範代ボイスの URL（先頭行）。ミュート時は呼び出し側が無視する。 */
  readonly voiceSrc: string;
}

export interface UseCompanionCoachResult {
  /** 直近の声かけ。同時に複数起きても最新の1件だけを保持する（ui-design.md §5.7）。 */
  readonly activeReaction: ActiveReaction | null;
  readonly revealedHintCount: number;
  readonly hintCount: number;
  readonly canRequestHint: boolean;
  /** 「師範代に聞く」押下時に呼ぶ。次の段階の助言を1つ開示する（ペナルティなし・F-007）。 */
  readonly requestHint: () => void;
  /** `useDrillSession`の`onOutcome`へそのまま渡す。1打ごとに無操作タイマーを再始動する。 */
  readonly onOutcome: (outcome: KeystrokeOutcome) => void;
}

let nextReactionId = 0;

/**
 * 「詰まったときに助けが来る」体験（F-007）を束ねるフック。演習中の声かけ
 * （`CompanionReaction`を表から引くだけ）・90秒無操作/3回連続未達での自発的な声かけ・
 * 助言の段階的開示を、演習（`drillId`）の切り替えに追従して管理する。
 *
 * 表示（`Companion.tsx`）とは分離し、判定ロジックのみをここに置く。
 */
export function useCompanionCoach(
  reactions: readonly CompanionReaction[],
  hints: readonly Hint[],
  drillId: string,
  levelId: string,
): UseCompanionCoachResult {
  const verdict = useDrillStore((state) => state.verdict);
  const revealedHintCount = useDrillStore((state) => state.revealedHintCount);
  const setRevealedHintCount = useDrillStore((state) => state.setRevealedHintCount);

  const [activeReaction, setActiveReaction] = useState<ActiveReaction | null>(null);
  const repeatedFailCountRef = useRef(0);
  const idleTimerRef = useRef<number | undefined>(undefined);
  /**
   * 場面ごとの発生回数。同じ場面で毎回同じ台詞にならないよう、教材が持つ複数の言い回しを
   * この回数で巡回させる。段に入っている間は演習をまたいで数え続ける（演習が変わるたびに
   * 0へ戻すと、同じ台詞ばかりが出てしまうため）。
   */
  const occurrenceRef = useRef<Map<ReactionTrigger, number>>(new Map());
  const bubbleTimerRef = useRef<number | undefined>(undefined);
  const chatterTimerRef = useRef<number | undefined>(undefined);
  /** 次に挟むのは雑談かコツか。交互に出して、どちらかへ偏らせない。 */
  const nextChatterRef = useRef<ReactionTrigger>("tip");
  /** 直近の打鍵時刻。手が動いている間だけ雑談を挟む（止まっている人へは`idle`が声をかける）。 */
  const lastActivityAtRef = useRef(Date.now());

  const fire = useCallback(
    (trigger: ReactionTrigger) => {
      const occurrence = occurrenceRef.current.get(trigger) ?? 0;
      const candidates = reactions.filter((reaction) => reaction.trigger === trigger);
      const reaction = findCompanionReaction(reactions, trigger, occurrence);
      if (!reaction || candidates.length === 0) {
        return;
      }
      const variantIndex = occurrence % candidates.length;
      // 雑談・コツは同じ並びで繰り返さないよう、開始位置を毎回散らす。
      const nextOccurrence =
        trigger === "chat" || trigger === "tip"
          ? occurrence + 1 + Math.floor(Math.random() * 2)
          : occurrence + 1;
      occurrenceRef.current.set(trigger, nextOccurrence);
      nextReactionId += 1;
      setActiveReaction({
        id: nextReactionId,
        reaction,
        voiceSrc: reactionVoiceAssetPath(levelId, trigger, variantIndex, 0),
      });

      if (bubbleTimerRef.current !== undefined) {
        window.clearTimeout(bubbleTimerRef.current);
      }
      bubbleTimerRef.current = window.setTimeout(
        () => setActiveReaction(null),
        bubbleLifetimeMs(reaction),
      );
    },
    [reactions, levelId],
  );

  /**
   * 稽古の合間に雑談・その段のコツを挟む。声かけが立つたびに数え直すため、
   * 判定や未習の打鍵で声がかかった直後に重ねて喋ることはない。
   */
  const resetChatterTimer = useCallback(() => {
    if (chatterTimerRef.current !== undefined) {
      window.clearTimeout(chatterTimerRef.current);
    }
    chatterTimerRef.current = window.setTimeout(() => {
      // 手が止まっている間は割り込まない（その場面は`idle`の受け持ち）。
      if (Date.now() - lastActivityAtRef.current < CHATTER_INTERVAL_MS) {
        const trigger = nextChatterRef.current;
        nextChatterRef.current = trigger === "tip" ? "chat" : "tip";
        fire(trigger);
      }
      resetChatterTimer();
    }, CHATTER_INTERVAL_MS);
  }, [fire]);

  const resetIdleTimer = useCallback(() => {
    if (idleTimerRef.current !== undefined) {
      window.clearTimeout(idleTimerRef.current);
    }
    idleTimerRef.current = window.setTimeout(() => fire("idle"), IDLE_TIMEOUT_MS);
  }, [fire]);

  // 演習が変わるたび（マウント含む）、声かけ・連続未達数・各タイマーを初期化する。
  // biome-ignore lint/correctness/useExhaustiveDependencies: drillIdの変化そのものが初期化のトリガー
  useEffect(() => {
    repeatedFailCountRef.current = 0;
    setActiveReaction(null);
    resetIdleTimer();
    resetChatterTimer();
    return () => {
      for (const timer of [idleTimerRef, chatterTimerRef, bubbleTimerRef]) {
        if (timer.current !== undefined) {
          window.clearTimeout(timer.current);
        }
      }
    };
  }, [drillId, resetIdleTimer, resetChatterTimer]);

  useEffect(() => {
    if (verdict?.kind === "passed") {
      repeatedFailCountRef.current = 0;
      fire("pass");
    } else if (verdict?.kind === "off-technique") {
      // 的には届いている。黙って落とすと「壊れている」と受け取られるため、必ず声をかける。
      fire("off-technique");
    } else if (verdict?.kind === "over-limit") {
      repeatedFailCountRef.current += 1;
      fire(
        repeatedFailCountRef.current >= REPEATED_FAIL_THRESHOLD ? "repeated-fail" : "over-limit",
      );
    }
  }, [verdict, fire]);

  const onOutcome = useCallback(
    (outcome: KeystrokeOutcome) => {
      lastActivityAtRef.current = Date.now();
      resetIdleTimer();
      if (outcome.kind === "unlearned") {
        fire("unlearned-key");
        resetChatterTimer();
      }
    },
    [resetIdleTimer, resetChatterTimer, fire],
  );

  const requestHint = useCallback(() => {
    resetIdleTimer();
    setRevealedHintCount(Math.min(revealedHintCount + 1, hints.length));
  }, [resetIdleTimer, setRevealedHintCount, revealedHintCount, hints.length]);

  return {
    activeReaction,
    revealedHintCount,
    hintCount: hints.length,
    canRequestHint: revealedHintCount < hints.length,
    requestHint,
    onOutcome,
  };
}
